import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SmartintegService } from '../../../../services/smartinteg.service';
import { ToastService } from '../../../../services/toast.service';
import { 
  Processo, 
  Recurso, 
  TipoRecurso, 
  TipoComando, 
  PublicacaoConfig, 
  DirecaoPublicacao, 
  FonteDadosPublicacao,
  DestinoRecebimento,
  ModoProcessamento,
  FormatoResposta,
  Comando,
  ComandoMapeamento,
  ProvedorConexao,
  ProvedorEndpoint
} from '../../../../types/smartinteg';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { switchMap, of, forkJoin, map, catchError } from 'rxjs';

@Component({
  selector: 'app-processo-fluxo',
  templateUrl: './processo-fluxo.component.html',
  styleUrls: ['./processo-fluxo.component.css'],
  providers: [ConfirmationService]
})
export class ProcessoFluxoComponent implements OnInit {

  processoId!: string;
  processo: Processo | null = null;
  recursos: Recurso[] = [];
  loading: boolean = true;
  
  // Seleção e Detalhes
  recursoSelecionado: any = null;
  comandoDetalhe: any = null;
  mapeamentoDetalhe: any = null;
  publicacaoConfig: any = null;
  loadingDetalhes: boolean = false;
  isNew: boolean = false;
  
  // Menus e Opções
  tiposRecursoOptions: MenuItem[] = [];
  opcoesDirecao = [
    { label: 'Envio (Outbound)', value: DirecaoPublicacao.OUTBOUND },
    { label: 'Recebimento (Inbound)', value: DirecaoPublicacao.INBOUND },
    { label: 'Bidirecional', value: DirecaoPublicacao.BIDIRECIONAL }
  ];
  opcoesFonteDados = [
    { label: 'Registros Brutos', value: FonteDadosPublicacao.REGISTROS_BRUTOS },
    { label: 'Registros Transformados', value: FonteDadosPublicacao.REGISTROS_TRANSFORMADOS },
    { label: 'Registros de Visualização', value: FonteDadosPublicacao.REGISTROS_VISUALIZACAO }
  ];
  opcoesDestinoRecebimento = [
    { label: 'Banco de Dados', value: DestinoRecebimento.BANCO_DADOS },
    { label: 'API Externa', value: DestinoRecebimento.API_EXTERNA },
    { label: 'Fila de Processamento', value: DestinoRecebimento.FILA_PROCESSAMENTO }
  ];
  opcoesModoProcessamento = [
    { label: 'Síncrono', value: ModoProcessamento.SINCRONO },
    { label: 'Assíncrono', value: ModoProcessamento.ASSINCRONO }
  ];
  opcoesFormatoResposta = [
    { label: 'JSON', value: FormatoResposta.JSON }
  ];
  opcoesTipoComando = [
    { label: 'SQL (Banco de Dados)', value: TipoComando.DATABASE_SQL },
    { label: 'Endpoint (API REST)', value: TipoComando.API_ENDPOINT }
  ];

  // Listas de apoio para seleção em Inbound e Comandos
  todosComandos: Comando[] = [];
  todosMapeamentos: ComandoMapeamento[] = [];
  todasConexoes: ProvedorConexao[] = [];
  todosEndpoints: ProvedorEndpoint[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private smartintegService: SmartintegService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService
  ) { 
    this.tiposRecursoOptions = [
      { label: 'Importação', icon: 'pi pi-cloud-download', command: () => this.iniciarCriacaoRecurso(TipoRecurso.IMPORTACAO) },
      { label: 'Processamento', icon: 'pi pi-cog', command: () => this.iniciarCriacaoRecurso(TipoRecurso.PROCESSAMENTO) },
      { label: 'Visualização', icon: 'pi pi-eye', command: () => this.iniciarCriacaoRecurso(TipoRecurso.VISUALIZACAO) },
      { label: 'Exportação', icon: 'pi pi-cloud-upload', command: () => this.iniciarCriacaoRecurso(TipoRecurso.EXPORTACAO) },
      { label: 'Publicação', icon: 'pi pi-globe', command: () => this.iniciarCriacaoRecurso(TipoRecurso.PUBLICACAO) }
    ];
  }

  ngOnInit(): void {
    this.processoId = this.route.snapshot.paramMap.get('processoId')!;
    if (this.processoId) {
      this.carregarProcesso();
      this.carregarRecursos();
      this.carregarAuxiliares();
    } else {
      this.toastService.erro('Erro', 'ID do processo não encontrado.');
      this.voltar();
    }
  }

  carregarAuxiliares(): void {
    this.smartintegService.listarComandos().subscribe(data => this.todosComandos = data);
    this.smartintegService.listarMapeamentos().subscribe(data => this.todosMapeamentos = data);
    this.smartintegService.listarProvedores().pipe(
      switchMap(provedores => {
        const conexoesObs = provedores.map(p => this.smartintegService.listarConexoesPorProvedor(p.id));
        return conexoesObs.length > 0 ? forkJoin(conexoesObs) : of([]);
      })
    ).subscribe(res => {
      this.todasConexoes = res.flat();
    });
  }

  onConexaoChange(conexaoId: string): void {
    if (conexaoId) {
      this.smartintegService.listarEndpointsPorConexao(conexaoId).subscribe(data => {
        this.todosEndpoints = data;
      });
    } else {
      this.todosEndpoints = [];
    }
  }

  carregarProcesso(): void {
    this.smartintegService.buscarProcesso(this.processoId).subscribe({
      next: (data) => this.processo = data
    });
  }

  carregarRecursos(): void {
    this.loading = true;
    this.smartintegService.listarRecursosPorProcesso(this.processoId).subscribe({
      next: (data) => {
        this.recursos = data.sort((a, b) => a.ordemExecucao - b.ordemExecucao);
        this.loading = false;
      },
      error: (err) => {
        this.toastService.erro('Erro', 'Falha ao carregar recursos do processo.');
        this.loading = false;
      }
    });
  }

  selecionarRecurso(recurso: Recurso): void {
    this.recursoSelecionado = { ...recurso };
    this.isNew = false;
    this.comandoDetalhe = null;
    this.mapeamentoDetalhe = null;
    this.publicacaoConfig = null;
    this.loadingDetalhes = true;

    const observables = [];

    // 1. Carregar Configuração de Publicação (Publicação ou Visualização)
    if (recurso.tipoRecurso === TipoRecurso.PUBLICACAO || recurso.tipoRecurso === TipoRecurso.VISUALIZACAO || recurso.temPublicacaoConfig) {
      observables.push(
        this.smartintegService.buscarPublicacaoPorRecurso(recurso.id).pipe(
          map(configs => ({ type: 'publicacao', data: configs.length > 0 ? { ...configs[0] } : this.initPublicacaoConfig(recurso.id) })),
          catchError(() => of({ type: 'publicacao', data: this.initPublicacaoConfig(recurso.id) }))
        )
      );
    }

    // 2. Carregar Comando (Importação, Exportação ou Genérico)
    if (recurso.tipoRecurso === TipoRecurso.IMPORTACAO || recurso.tipoRecurso === TipoRecurso.EXPORTACAO || recurso.temComando) {
      observables.push(
        this.smartintegService.listarComandosPorRecurso(recurso.id).pipe(
          map(comandos => ({ type: 'comando', data: comandos.length > 0 ? { ...comandos[0] } : this.initComando(recurso.id) })),
          catchError(() => of({ type: 'comando', data: null }))
        )
      );
    }

    // 3. Carregar Mapeamento (Processamento, Visualização ou Genérico)
    if (recurso.tipoRecurso === TipoRecurso.PROCESSAMENTO || recurso.tipoRecurso === TipoRecurso.VISUALIZACAO || recurso.temComandoMapeamento) {
      observables.push(
        this.smartintegService.listarMapeamentosPorRecurso(recurso.id).pipe(
          map(mapeamentos => ({ type: 'mapeamento', data: mapeamentos.length > 0 ? { ...mapeamentos[0] } : this.initMapeamento(recurso.id) })),
          catchError(() => of({ type: 'mapeamento', data: null }))
        )
      );
    }

    if (observables.length > 0) {
      forkJoin(observables).subscribe({
        next: (results: any[]) => {
          results.forEach(res => {
            if (res.type === 'publicacao' && res.data) {
              this.publicacaoConfig = res.data;
              // Tratamento de nulls do backend
              if (this.publicacaoConfig.ativo === null || this.publicacaoConfig.ativo === undefined) {
                this.publicacaoConfig.ativo = true;
              }
              if (!this.publicacaoConfig.operadoresFiltro) this.publicacaoConfig.operadoresFiltro = {};
              if (!this.publicacaoConfig.filtrosFixos) this.publicacaoConfig.filtrosFixos = {};
              if (!this.publicacaoConfig.transformacoesSaida) this.publicacaoConfig.transformacoesSaida = {};
              if (!this.publicacaoConfig.camposRetorno) this.publicacaoConfig.camposRetorno = [];
              if (!this.publicacaoConfig.camposFiltro) this.publicacaoConfig.camposFiltro = [];
            }
            if (res.type === 'comando' && res.data) {
              this.comandoDetalhe = res.data;
              if (this.comandoDetalhe.conexaoId) {
                this.onConexaoChange(this.comandoDetalhe.conexaoId);
              }
            }
            if (res.type === 'mapeamento' && res.data) {
              this.mapeamentoDetalhe = res.data;
            }
          });
          this.loadingDetalhes = false;
        },
        error: (err) => {
          console.error('Erro ao carregar detalhes do recurso', err);
          this.loadingDetalhes = false;
          this.toastService.erro('Erro', 'Falha ao carregar detalhes do recurso.');
        }
      });
    } else {
      this.loadingDetalhes = false;
    }
  }

  iniciarCriacaoRecurso(tipo: TipoRecurso): void {
    this.isNew = true;
    this.recursoSelecionado = {
      processoId: this.processoId,
      tipoRecurso: tipo,
      nome: `Novo Recurso - ${tipo}`,
      descricao: '',
      ordemExecucao: this.recursos.length + 1,
      ativo: true,
      codigo: ''
    };
    
    this.comandoDetalhe = null;
    this.mapeamentoDetalhe = null;
    this.publicacaoConfig = null;
    this.loadingDetalhes = false;

    if (tipo === TipoRecurso.IMPORTACAO || tipo === TipoRecurso.EXPORTACAO) {
      this.comandoDetalhe = this.initComando(0);
    } else if (tipo === TipoRecurso.PROCESSAMENTO || tipo === TipoRecurso.VISUALIZACAO) {
      this.mapeamentoDetalhe = this.initMapeamento(0);
      // if (tipo === TipoRecurso.VISUALIZACAO) {
      //   this.publicacaoConfig = this.initPublicacaoConfig(0);
      // }
    } else if (tipo === TipoRecurso.PUBLICACAO) {
      this.publicacaoConfig = this.initPublicacaoConfig(0);
    }
  }

  initComando(recursoId: number): any {
    return {
      recursoId: recursoId,
      nome: 'Comando Padrão',
      descricao: '',
      tipo: TipoComando.DATABASE_SQL,
      comando: '',
      ativo: true,
      camposChave: [],
      parametros: {}
    };
  }

  initMapeamento(recursoId: number): any {
    return {
      recursoId: recursoId,
      nome: 'Mapeamento Padrão',
      descricao: '',
      mapeamentos: {},
      filtros: {},
      ativo: true
    };
  }

  initPublicacaoConfig(recursoId: number): any {
    return {
      recursoId: recursoId,
      nome: 'Configuração de Publicação',
      descricao: '',
      direcao: DirecaoPublicacao.OUTBOUND,
      fonteDados: FonteDadosPublicacao.REGISTROS_TRANSFORMADOS,
      camposRetorno: [],
      suportaPaginacao: true,
      limitePadrao: 50,
      limiteMaximo: 500,
      ordenacaoPadrao: '',
      camposFiltro: [],
      operadoresFiltro: {},
      filtrosFixos: {},
      transformacoesSaida: {},
      destinoDados: DestinoRecebimento.BANCO_DADOS,
      mapeamentoRecebimentoId: null,
      comandoDestinoId: null,
      schemaEntrada: null,
      camposObrigatorios: [],
      validarSchema: true,
      permitirCamposExtras: false,
      modoProcessamento: ModoProcessamento.SINCRONO,
      formatoResposta: FormatoResposta.JSON,
      ativo: true
    };
  }

  salvarRecurso(): void {
    this.loadingDetalhes = true;

    if (this.isNew) {
      this.salvarNovoRecurso();
    } else {
      this.atualizarRecursoExistente();
    }
  }

  salvarNovoRecurso(): void {
    // Monta o payload conforme a especificação FLAT v1.0
    let payload: any = {
      processoId: this.processoId,
      tipoRecurso: this.recursoSelecionado.tipoRecurso,
      nomeRecurso: this.recursoSelecionado.nome,
      descricaoRecurso: this.recursoSelecionado.descricao,
      ordemExecucao: this.recursoSelecionado.ordemExecucao,
      codigo: this.recursoSelecionado.codigo
    };

    let obs$: any;
    switch (this.recursoSelecionado.tipoRecurso) {
      case TipoRecurso.IMPORTACAO:
      case TipoRecurso.EXPORTACAO:
        payload = {
          ...payload,
          nomeComando: this.comandoDetalhe.nome,
          descricaoComando: this.comandoDetalhe.descricao,
          ordemComando: 1,
          tipoComando: this.comandoDetalhe.tipo,
          conexaoId: this.comandoDetalhe.conexaoId,
          endpointId: this.comandoDetalhe.endpointId,
          comando: this.comandoDetalhe.comando,
          parametros: this.comandoDetalhe.parametros,
          camposChave: this.comandoDetalhe.camposChave,
          camposIgnorarComparacao: this.comandoDetalhe.camposIgnorarComparacao
        };
        obs$ = this.recursoSelecionado.tipoRecurso === TipoRecurso.IMPORTACAO 
          ? this.smartintegService.criarRecursoImportacaoCompleto(payload)
          : this.smartintegService.criarRecursoExportacaoCompleto(payload);
        break;

      case TipoRecurso.PROCESSAMENTO:
      case TipoRecurso.VISUALIZACAO:
        payload = {
          ...payload,
          comandoOrigemId: this.mapeamentoDetalhe.comandoOrigemId,
          nomeMapeamento: this.mapeamentoDetalhe.nome,
          descricaoMapeamento: this.mapeamentoDetalhe.descricao,
          mapeamentos: this.mapeamentoDetalhe.mapeamentos,
          filtros: this.mapeamentoDetalhe.filtros
        };
        obs$ = this.recursoSelecionado.tipoRecurso === TipoRecurso.PROCESSAMENTO
          ? this.smartintegService.criarRecursoProcessamentoCompleto(payload)
          : this.smartintegService.criarRecursoVisualizacaoCompleto(payload);
        break;

      case TipoRecurso.PUBLICACAO:
        const configPayload = {
          nome: this.publicacaoConfig.nome,
          descricao: this.publicacaoConfig.descricao,
          direcao: this.publicacaoConfig.direcao,
          fonteDados: this.publicacaoConfig.fonteDados,
          camposRetorno: this.publicacaoConfig.camposRetorno,
          suportaPaginacao: this.publicacaoConfig.suportaPaginacao,
          limitePadrao: this.publicacaoConfig.limitePadrao,
          limiteMaximo: this.publicacaoConfig.limiteMaximo,
          ordenacaoPadrao: this.publicacaoConfig.ordenacaoPadrao,
          camposFiltro: this.publicacaoConfig.camposFiltro,
          operadoresFiltro: this.publicacaoConfig.operadoresFiltro,
          filtrosFixos: this.publicacaoConfig.filtrosFixos,
          transformacoesSaida: this.publicacaoConfig.transformacoesSaida,
          destinoDados: this.publicacaoConfig.destinoDados,
          mapeamentoRecebimentoId: this.publicacaoConfig.mapeamentoRecebimentoId,
          comandoDestinoId: this.publicacaoConfig.comandoDestinoId,
          schemaEntrada: this.publicacaoConfig.schemaEntrada,
          camposObrigatorios: this.publicacaoConfig.camposObrigatorios,
          validarSchema: this.publicacaoConfig.validarSchema,
          permitirCamposExtras: this.publicacaoConfig.permitirCamposExtras,
          modoProcessamento: this.publicacaoConfig.modoProcessamento,
          respostaSucesso: this.publicacaoConfig.respostaSucesso,
          respostaErro: this.publicacaoConfig.respostaErro,
          formatoResposta: this.publicacaoConfig.formatoResposta,
          ativo: true
        };

        obs$ = this.smartintegService.criarPublicacao(configPayload).pipe(
          switchMap((configCriada: PublicacaoConfig) => {
            const recursoPayload = {
              processoId: this.processoId,
              tipoRecurso: TipoRecurso.PUBLICACAO,
              nome: this.recursoSelecionado.nome,
              descricao: this.recursoSelecionado.descricao,
              ordemExecucao: this.recursoSelecionado.ordemExecucao,
              codigo: this.recursoSelecionado.codigo,
              publicacaoConfigId: configCriada.id
            };
            return this.smartintegService.criarRecurso(recursoPayload);
          })
        );
        break;

      default:
        obs$ = of(null);
    }

    obs$.subscribe({
      next: (res: any) => {
        this.toastService.sucesso('Sucesso', 'Recurso criado com sucesso.');
        this.loadingDetalhes = false;
        this.recursoSelecionado = null;
        this.carregarRecursos();
      },
      error: (err: any) => {
        this.toastService.erro('Erro', 'Falha ao criar recurso.');
        console.error(err);
        this.loadingDetalhes = false;
      }
    });
  }

  atualizarRecursoExistente(): void {
    const updateRecurso$ = this.smartintegService.atualizarRecurso(this.recursoSelecionado);
    
    let updateDetalhe$: any = of(null);
    
    if (this.comandoDetalhe && this.comandoDetalhe.id) {
      updateDetalhe$ = this.smartintegService.atualizarComando(this.comandoDetalhe);
    } else if (this.mapeamentoDetalhe && this.mapeamentoDetalhe.id) {
      updateDetalhe$ = this.smartintegService.atualizarMapeamento(this.mapeamentoDetalhe);
    } else if (this.publicacaoConfig && this.publicacaoConfig.id) {
      updateDetalhe$ = this.smartintegService.atualizarPublicacao(this.publicacaoConfig);
    }

    forkJoin([updateRecurso$, updateDetalhe$]).subscribe({
      next: () => {
        this.toastService.sucesso('Sucesso', 'Recurso atualizado.');
        this.loadingDetalhes = false;
        this.recursoSelecionado = null;
        this.carregarRecursos();
      },
      error: (err: any) => {
        this.toastService.erro('Erro', 'Falha ao atualizar recurso.');
        console.error(err);
        this.loadingDetalhes = false;
      }
    });
  }

  cancelarEdicao(): void {
    this.recursoSelecionado = null;
    this.isNew = false;
  }

  onReorder(event: any): void {
    this.loading = true;
    const updates: any[] = [];
    
    this.recursos.forEach((rec, index) => {
      const novaOrdem = index + 1;
      if (rec.ordemExecucao !== novaOrdem) {
        rec.ordemExecucao = novaOrdem;
        updates.push(this.smartintegService.reordenarRecurso(rec.id, novaOrdem));
      }
    });

    if (updates.length > 0) {
      forkJoin(updates).subscribe({
        next: () => {
          this.toastService.sucesso('Sucesso', 'Ordem atualizada.');
          this.loading = false;
        },
        error: () => {
          this.toastService.erro('Erro', 'Falha ao salvar nova ordem.');
          this.loading = false;
          this.carregarRecursos();
        }
      });
    } else {
      this.loading = false;
    }
  }
  
  excluirRecurso(recurso: Recurso): void {
    this.confirmationService.confirm({
      message: `Tem certeza que deseja remover o recurso "${recurso.nome}"?`,
      header: 'Confirmar Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: () => {
        this.smartintegService.deletarRecurso(recurso.id).subscribe({
          next: () => {
            this.toastService.sucesso('Sucesso', 'Recurso removido.');
            this.carregarRecursos();
            if (this.recursoSelecionado?.id === recurso.id) {
              this.recursoSelecionado = null;
            }
          },
          error: () => this.toastService.erro('Erro', 'Falha ao remover recurso.')
        });
      }
    });
  }

  getIconeRecurso(tipo: TipoRecurso): string {
    switch (tipo) {
      case TipoRecurso.IMPORTACAO: return 'pi pi-cloud-download';
      case TipoRecurso.PROCESSAMENTO: return 'pi pi-cog';
      case TipoRecurso.VISUALIZACAO: return 'pi pi-eye';
      case TipoRecurso.EXPORTACAO: return 'pi pi-cloud-upload';
      case TipoRecurso.PUBLICACAO: return 'pi pi-globe';
      default: return 'pi pi-question';
    }
  }
  
  irParaWizard(): void {
    this.router.navigate(['/smartinteg/processos', this.processoId, 'wizard']);
  }

  executarProcesso(): void {
    this.router.navigate(['/smartinteg/processos', this.processoId, 'executar']);
  }

  voltar(): void {
    this.router.navigate(['/smartinteg/processos']);
  }
}
